import { ErrorEventsEnum } from '@northladder/utilities';
import { logErrorToSentry } from '@northladder/web-utils';

import { wait } from './audioPlaybackUtils';

interface IWebkitWindow {
  webkitAudioContext: typeof AudioContext;
}

/**
 * Gets the AudioContext class from window (supports standard and webkit prefix)
 */
export function getAudioContextClass(): typeof AudioContext | null {
  if (typeof window === 'undefined') return null;

  return (
    window.AudioContext ||
    (window as unknown as IWebkitWindow).webkitAudioContext ||
    null
  );
}

/**
 * Creates an AudioContext instance with browser compatibility.
 * Throws error if AudioContext is not available.
 */
export function createAudioContext(
  browserNotSupportedErrorMessage?: string
): AudioContext {
  const AudioContextClass = getAudioContextClass();

  if (!AudioContextClass) {
    throw new Error(
      browserNotSupportedErrorMessage ||
        'AudioContext is not supported in this browser'
    );
  }

  return new AudioContextClass();
}

/**
 * Resumes AudioContext if it's suspended (required for iOS Safari)
 * Throws error if AudioContext is not available or resume fails
 */
export async function resumeAudioContextIfSuspended(
  browserNotSupportedErrorMessage?: string
): Promise<void> {
  const audioContext = createAudioContext(browserNotSupportedErrorMessage);

  try {
    if (audioContext.state === 'suspended') {
      try {
        await audioContext.resume();
      } catch (resumeError) {
        logErrorToSentry({
          error:
            resumeError instanceof Error
              ? resumeError
              : new Error(String(resumeError)),
          errorKey: ErrorEventsEnum.ERROR_IN_PERMISSION,
          errorName: 'AUDIOCONTEXT_RESUME_FAILED',
          source: 'audioContextUtils.resumeAudioContextIfSuspended',
        });
      }
    }

    await audioContext.close();
  } catch (error) {
    await audioContext.close().catch(() => {});

    throw new Error(browserNotSupportedErrorMessage);
  }
}

/**
 * Resumes an AudioContext with retry logic and timeout
 */
async function resumeAudioContextWithRetry(
  audioContext: AudioContext,
  retryCount: number,
  maxRetries: number,
  timeoutMs: number,
  retryDelayMs: number
): Promise<void> {
  if (audioContext.state !== 'suspended' || retryCount >= maxRetries) return;

  try {
    const resumePromise = audioContext.resume();
    const timeoutPromise = new Promise<void>((_, reject) => {
      setTimeout(() => {
        reject(new Error('AudioContext resume timeout'));
      }, timeoutMs);
    });

    await Promise.race([resumePromise, timeoutPromise]);
  } catch (error) {
    const lastError = error instanceof Error ? error : new Error(String(error));

    logErrorToSentry({
      error: lastError,
      errorKey: ErrorEventsEnum.ERROR_IN_PERMISSION,
      errorName: 'AUDIOCONTEXT_RESUME_FAILED',
      source: 'audioContextUtils.resumeAudioContextWithRetry',
    });

    if (retryCount < maxRetries - 1 && audioContext.state === 'suspended') {
      await wait(retryDelayMs).promise;
      await resumeAudioContextWithRetry(
        audioContext,
        retryCount + 1,
        maxRetries,
        timeoutMs,
        retryDelayMs
      );

      return;
    }

    if (audioContext.state === 'suspended') {
      logErrorToSentry({
        error: new Error(
          `AudioContext failed to resume after ${maxRetries} attempts: ${lastError.message}`
        ),
        errorKey: ErrorEventsEnum.ERROR_IN_PERMISSION,
        errorName: 'AUDIOCONTEXT_RESUME_FINAL_FAILURE',
        source: 'audioContextUtils.resumeAudioContextWithRetry',
      });
    }
  }
}

/**
 * Creates and resumes an AudioContext instance
 * Returns null if AudioContext is not available
 * @param maxRetries - Number of retry attempts for resuming (0 = no retries)
 */
export async function createAndResumeAudioContext(
  maxRetries = 0
): Promise<AudioContext | null> {
  const AudioContextClass = getAudioContextClass();

  if (!AudioContextClass) return null;

  const audioContext = createAudioContext();

  if (audioContext.state === 'suspended') {
    if (maxRetries > 0) {
      await resumeAudioContextWithRetry(audioContext, 0, maxRetries, 3000, 500);
    } else {
      try {
        await audioContext.resume();
      } catch (resumeError) {
        logErrorToSentry({
          error:
            resumeError instanceof Error
              ? resumeError
              : new Error(String(resumeError)),
          errorKey: ErrorEventsEnum.ERROR_IN_PERMISSION,
          errorName: 'AUDIOCONTEXT_RESUME_FAILED',
          source: 'audioContextUtils.createAndResumeAudioContext',
        });
      }
    }
  }

  return audioContext;
}

/**
 * Creates an AudioContext instance and resumes it if suspended with retry logic.
 * Throws error if AudioContext is not available.
 * @param browserNotSupportedErrorMessage - Error message to throw if AudioContext is not available
 * @param maxRetries - Number of retry attempts for resuming (default: 3)
 */
export async function createAndResumeAudioContextWithRetry(
  browserNotSupportedErrorMessage?: string,
  maxRetries = 3
): Promise<AudioContext> {
  const audioContext = createAudioContext(browserNotSupportedErrorMessage);

  if (audioContext.state === 'suspended') {
    await resumeAudioContextWithRetry(audioContext, 0, maxRetries, 3000, 500);
  }

  return audioContext;
}

/**
 * Initializes AudioContext on user interaction for Safari compatibility
 */
export async function initializeAudioContextOnInteraction(): Promise<void> {
  try {
    const AudioContextClass = getAudioContextClass();

    if (AudioContextClass) {
      const testContext = createAudioContext();

      if (testContext.state === 'suspended') {
        await testContext.resume();
      }

      await testContext.close();
    }
  } catch (error) {
    logErrorToSentry({
      error: error instanceof Error ? error : new Error(String(error)),
      errorKey: ErrorEventsEnum.ERROR_IN_PERMISSION,
      errorName: 'AUDIOCONTEXT_INITIALIZATION_FAILED',
      source: 'audioContextUtils.initializeAudioContextOnInteraction',
    });
  }
}
